import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { FaUser, FaEnvelope, FaPhone, FaBriefcase, FaKey, FaCheckCircle, FaBuilding } from 'react-icons/fa';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const EmployeeRegistration: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [businessData, setBusinessData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    registrationCode: '',
    password: ''
  });

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        if (!businessId) {
          setError(t('employee.invalidLink'));
          setLoading(false);
          return;
        }

        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        if (!businessDoc.exists()) {
          setError(t('employee.businessNotFound'));
          setLoading(false);
          return;
        }

        setBusinessData({ id: businessDoc.id, ...businessDoc.data() });
      } catch (err) {
        console.error('Error fetching business:', err);
        setError(t('employee.errorLoadingBusiness'));
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [businessId, t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = (): string | null => {
    if (!formData.firstName.trim()) return t('employee.firstNameRequired');
    if (!formData.lastName.trim()) return t('employee.lastNameRequired');
    if (!formData.email.trim()) return t('employee.emailRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return t('employee.emailInvalid');
    if (!formData.phone.trim()) return t('employee.phoneRequired');
    if (!formData.password || formData.password.length < 6) return t('employee.passwordMinLength');
    if (!formData.registrationCode.trim()) return t('employee.registrationCodeRequired');
    if (formData.registrationCode !== businessData?.employeeRegistrationCode) {
      return t('employee.invalidRegistrationCode');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);

    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const userId = userCredential.user.uid;

      // Crear documento en la colección users usando el UID como ID del documento
      await setDoc(doc(db, 'users', userId), {
        uid: userId,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        title: formData.title || '',
        role: 'employee',
        businessId: businessId,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      });

      console.log('✅ Employee registered successfully:', userId);
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err: any) {
      console.error('❌ Error registering employee:', err);
      
      // Si el error es de email duplicado, eliminar el usuario de Auth si fue creado
      if (err.code === 'auth/email-already-in-use') {
        setError(t('employee.emailAlreadyInUse'));
      } else if (err.code === 'permission-denied') {
        setError('Error de permisos. Por favor, contacta al administrador.');
      } else {
        setError(t('employee.registrationError') + ': ' + err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center p-8">
        <div className="bg-white rounded-3xl p-12 shadow-2xl border border-white/40 max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-green-600 text-4xl" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('employee.registrationSuccess')}</h2>
          <p className="text-gray-600 mb-2">{t('employee.pendingApproval')}</p>
          <p className="text-gray-500 text-sm">{t('employee.redirecting')}</p>
        </div>
      </div>
    );
  }

  if (error && !businessData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center p-8">
        <div className="bg-red-50 border border-red-200 rounded-3xl p-8 max-w-md text-center">
          <p className="text-red-700 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8 font-sans">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40 mb-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FaBuilding className="text-white text-3xl" />
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
            {t('employee.joinTeam')}
          </h1>
          <p className="text-xl text-gray-600 font-medium">{businessData?.companyName}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40">
          <h2 className="text-2xl font-bold text-red-600 mb-6">{t('employee.personalInformation')}</h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FaUser className="text-red-400" />
                {t('employee.firstName')} *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
                placeholder={t('employee.firstNamePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FaUser className="text-red-400" />
                {t('employee.lastName')} *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
                placeholder={t('employee.lastNamePlaceholder')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FaEnvelope className="text-red-400" />
                {t('employee.email')} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
                placeholder={t('employee.emailPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FaPhone className="text-red-400" />
                {t('employee.phone')} *
              </label>
              <PhoneInput
                international
                defaultCountry="US"
                value={formData.phone}
                onChange={(value) => setFormData(prev => ({ ...prev, phone: value || '' }))}
                className="w-full"
                numberInputProps={{
                  className: "w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
                }}
                style={{
                  '--PhoneInputCountryFlag-borderColor': 'transparent',
                  '--PhoneInputCountrySelectArrow-color': '#dc2626',
                  '--PhoneInput-color--focus': '#dc2626'
                } as React.CSSProperties}
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FaBriefcase className="text-red-400" />
              {t('employee.title')}
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
              placeholder={t('employee.titlePlaceholder')}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FaKey className="text-red-400" />
              {t('employee.password')} *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              autoComplete="new-password"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
              placeholder={t('employee.passwordPlaceholder')}
            />
            <p className="text-xs text-gray-500 mt-1">{t('employee.passwordHelp')}</p>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FaKey className="text-red-400" />
              {t('employee.registrationCode')} *
            </label>
            <input
              type="text"
              name="registrationCode"
              value={formData.registrationCode}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white/50 text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600 focus:bg-white/80"
              placeholder={t('employee.registrationCodePlaceholder')}
              maxLength={6}
            />
            <p className="text-xs text-gray-500 mt-1">{t('employee.registrationCodeHelp')}</p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-6 py-4 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white text-lg font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {t('employee.registering')}
              </>
            ) : (
              <>
                <FaCheckCircle />
                {t('employee.register')}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EmployeeRegistration;
